import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, Globe2Icon, MapPinIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
type Company = {
  name: string;
  logo?: string;
  fill?: boolean;
};
const COMPANIES: Company[] = [{
  name: 'GSK',
  logo: 'https://cdn.magicpatterns.com/uploads/w5GmZPzAqkJWp5ESa9Cmiv/Screenshot_2026-10-02_at_09.52.02.png',
  fill: true
}, {
  name: 'LEO Pharma',
  logo: 'https://cdn.magicpatterns.com/uploads/qe6bAu8fK165LTd9pa9Tmm/Screenshot_2026-08-20_at_21.22.35.png'
}, {
  name: '3M and Solventum',
  logo: 'https://cdn.magicpatterns.com/uploads/8a94AChcQKnfAzckma8QZD/Screenshot_2026-08-27_at_12.40.13.png'
}, {
  name: 'AbbVie',
  logo: 'https://cdn.magicpatterns.com/uploads/pKJuSjA5sXAAftL8qfcZvo/Screenshot_2026-08-27_at_12.39.43.png'
}, {
  name: 'Healthcare company',
  logo: 'https://cdn.magicpatterns.com/uploads/aQbK55PqMKmQ6yF9dTcsFH/Screenshot_2026-08-20_at_21.22.20.png'
}];
const REGIONS = ['Europe', 'Gulf', 'Africa', 'Asia', 'South America'] as const;
const MARKETS = ['Finland', 'Denmark', 'Germany', 'Poland', 'France', 'Netherlands', 'United Kingdom', 'Spain', 'Italy', 'Gulf', 'Morocco', 'Ivory Coast', 'Nigeria', 'South Africa', 'Kenya', 'Singapore', 'China', 'Peru', 'Brazil', 'Ireland', 'Sweden', 'Romania', 'Hungary'] as const;
export function SlideOperatingScale({}: SlideProps) {
  return <InteriorSlide surface data-id="element-325" data-mp="3szpxxy9k421l">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.5rem,2.4vw,2.5rem)] flex-shrink-0" data-id="element-326" data-mp="3t0j7t83tb7ny">
        <p className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-327" data-mp="6rqcyulfq80x">
          Global delivery experience
        </p>
        <h2 className="max-w-6xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-328" data-mp="1i87xpxfo6m7q">
          Campaign management{' '}
          <span className="text-[var(--brand)]" data-id="element-329" data-mp="ez57rdp1iegl">at scale.</span>
        </h2>
        <p className="mt-4 text-[clamp(1.1rem,1.9vw,1.85rem)] font-bold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-330" data-mp="juas482snl2k">
          New technology. Decades of know-how.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-331" data-mp="svr2wgxitp7b">
        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-black/5 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-4" data-id="element-332" data-mp="3scyrwb4ekbbg">
          <div className="flex items-start justify-between gap-4" data-id="element-333" data-mp="9zskwsy657e0">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-334" data-mp="39keem216sesr">
              <Building2Icon className="h-5 w-5" aria-hidden="true" data-id="element-335" data-mp="3dkok7dl2h373" />
            </div>
            <span className="text-[clamp(2.5rem,4vw,4rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-336" data-mp="362qgxqthaocb">
              5
            </span>
          </div>
          <h3 className="mt-4 text-xl font-bold text-[var(--fg)]" data-id="element-337" data-mp="2x228dbmutg6t">
            Companies with managed campaigns
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]" data-id="element-338" data-mp="2s2pe45tnl6u7">
            Selected organizations supported through continued campaign delivery.
          </p>
          <div className="mt-5 grid flex-1 grid-cols-2 gap-3" data-id="element-339" data-mp="2id5wt4ll9l1g">
            {COMPANIES.map((company, index) => <figure key={company.name} className={`flex min-h-[4rem] items-center justify-center overflow-hidden rounded-2xl border border-[var(--brand)]/10 ${company.fill ? 'relative bg-[var(--brand)]' : 'bg-white px-4 py-2'} ${index === COMPANIES.length - 1 ? 'col-span-2' : ''}`} data-id="element-340" data-mp="2de2wfxgk02zt" data-mpi={index}>
                {company.logo ? <img src={company.logo} alt={`${company.name} logo`} className={company.fill ? 'absolute inset-0 h-full w-full object-cover' : 'h-full max-h-14 w-full object-contain'} data-id="element-341" data-mp="1zejr1cf4n5r7" /> : <figcaption className="text-center text-[0.68rem] font-bold uppercase leading-tight tracking-[0.1em] text-[var(--muted)]" data-id="element-342" data-mp="89ynw3u32dcp">
                  {company.name}
                </figcaption>}
              </figure>)}
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.55
      }} className="relative flex min-h-0 flex-col justify-between overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.4rem,2.2vw,2.25rem)] text-white shadow-lg lg:col-span-3" data-id="element-343" data-mp="3gpieev3ddho2">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full border-[28px] border-white/10" data-id="element-344" data-mp="151aehx4c7rih" />
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-345" data-mp="1a0ncne0nsl0d">
            <div className="h-6 w-6" aria-hidden="true" data-id="element-346" data-mp="2uhmnquloeh55" />
          </div>
          <div className="relative" data-id="element-347" data-mp="2pj3450uk1hfy">
            <p className="text-[clamp(4.5rem,8vw,7.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-348" data-mp="2pk4fl6fhg5y7">
              22
            </p>
            <h3 className="mt-3 text-[clamp(1.25rem,2vw,1.75rem)] font-bold" data-id="element-349" data-mp="34a281ykg3ht7">
              Brands campaigned
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75" data-id="element-350" data-mp="3d8dvxstxg1hy">
              Across different portfolios, lifecycle stages, and HCP needs.
            </p>
            <p className="mt-5 border-t border-white/20 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-white/65" data-id="element-351" data-mp="389b3d7v4vnqv">
              Continued delivery experience
            </p>
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-[var(--brand)]/10 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-5" data-id="element-352" data-mp="1sssrbjspb3vl">
          <div className="flex items-start justify-between gap-4" data-id="element-353" data-mp="2hkhwr6mtuf2t">
            <div className="flex items-center gap-3" data-id="element-354" data-mp="2mit5fe1dyjjg">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-355" data-mp="2mgym245rizhh">
                <Globe2Icon className="h-5 w-5" aria-hidden="true" data-id="element-356" data-mp="2rf9wafzyzc08" />
              </span>
              <div data-id="element-357" data-mp="2wemo7f5p799o">
                <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-358" data-mp="30i2m0eti7fny">
                  5
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-359" data-mp="35acarrivu9on">
                  Regions covered
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-360" data-mp="3f8i7lupu0pmn">
              <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-361" data-mp="3f5dxkgpoqzo2">
                23
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-362" data-mp="18jzqqms9smle">
                Localizations
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-id="element-363" data-mp="yk8x0nbjabmd">
            {REGIONS.map(region => <span key={region} className="rounded-full border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-3 py-1.5 text-xs font-bold text-[var(--brand)]" data-id="element-364" data-mp="tlp94hddqprd">
                {region}
              </span>)}
          </div>

          <div className="mt-5 flex min-h-0 flex-1 flex-col rounded-2xl bg-[var(--surface)] p-4" data-id="element-365" data-mp="1m9tibcv5tkyt">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--fg)]" data-id="element-366" data-mp="1hj4m5k8jbztc">
              <MapPinIcon className="h-4 w-4 text-[var(--brand)]" aria-hidden="true" data-id="element-367" data-mp="1cl36786eogm3" />
              Market footprint
            </div>
            <ul className="grid flex-1 grid-cols-3 gap-x-4 gap-y-1.5" data-id="element-368" data-mp="2a0pcuhr02u9r">
              {MARKETS.map(market => <li key={market} className="flex items-center gap-1.5 text-[0.68rem] font-medium leading-tight text-[var(--muted)]" data-id="element-369" data-mp="258fimpiur81o">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-370" data-mp="1djt9d4ivpgq7" />
                  {market}
                </li>)}
            </ul>
          </div>
        </motion.article>
      </div>
    </InteriorSlide>;
}